// ============================================================================
// COMMUNITY CATEGORIES - Relaxing Routine App
// ============================================================================
// Centralized community post categories based on instructor types and class categories
// This ensures consistency across the application

export const COMMUNITY_CATEGORIES = [
  // Yoga & Movement
  "Yoga Practice",
  "Advanced Yoga",
  "Prenatal Yoga",

  // Breathing & Meditation
  "Pranayama & Breathing",
  "Meditation",
  "Mindfulness",

  // Fitness & Dance
  "Zumba & Dance Fitness",
  "Self Defense",
  "General Fitness",

  // Wellness & Health
  "Nutrition & Diet",
  "Ayurveda",
  "Mental Health",
  "Women's Wellness",

  // Lifestyle & Practice
  "Home Practice",
  "Daily Routine",
  "Equipment & Gear",
  "Class Experiences",
  "Instructor Tips",
  "Student Journey",

  // General
  "General Discussion",
  "Success Stories",
  "Questions & Help",
] as const;

// Type for the categories
export type CommunityCategory = (typeof COMMUNITY_CATEGORIES)[number];

// Category descriptions for UI tooltips/help text
export const CATEGORY_DESCRIPTIONS: Record<CommunityCategory, string> = {
  // Yoga & Movement
  "Yoga Practice": "Share your yoga experiences, poses, and practice tips",
  "Advanced Yoga":
    "Discuss challenging poses, advanced techniques, and progression",
  "Prenatal Yoga": "Safe yoga practices and experiences during pregnancy",

  // Breathing & Meditation
  "Pranayama & Breathing":
    "Breathing techniques, pranayama practices, and breath work",
  Meditation: "Meditation techniques, experiences, and mindfulness practices",
  Mindfulness:
    "Daily mindfulness, present moment awareness, and mental clarity",

  // Fitness & Dance
  "Zumba & Dance Fitness":
    "High-energy dance workouts and movement experiences",
  "Self Defense": "Self-defense techniques, safety tips, and empowerment",
  "General Fitness":
    "Overall fitness, strength training, and physical wellness",

  // Wellness & Health
  "Nutrition & Diet": "Healthy eating, meal planning, and nutritional guidance",
  Ayurveda: "Traditional Ayurvedic practices, herbs, and holistic healing",
  "Mental Health":
    "Emotional wellbeing, stress management, and mental wellness",
  "Women's Wellness": "Health topics specific to women's needs and experiences",

  // Lifestyle & Practice
  "Home Practice": "Tips for practicing yoga and wellness at home",
  "Daily Routine": "Incorporating wellness practices into daily life",
  "Equipment & Gear":
    "Reviews and recommendations for yoga and fitness equipment",
  "Class Experiences": "Share experiences from classes and workshops",
  "Instructor Tips": "Guidance and advice from certified instructors",
  "Student Journey": "Personal wellness journeys and transformations",

  // General
  "General Discussion": "Open discussions about wellness and lifestyle",
  "Success Stories": "Celebrate achievements and inspire others",
  "Questions & Help": "Ask questions and seek help from the community",
};

// Category groups for better UI organization
export const CATEGORY_GROUPS = {
  "Yoga & Movement": [
    "Yoga Practice",
    "Advanced Yoga",
    "Prenatal Yoga",
    "Senior Yoga",
    "Kids Yoga",
  ],
  "Breathing & Meditation": [
    "Pranayama & Breathing",
    "Meditation",
    "Mindfulness",
  ],
  "Fitness & Dance": [
    "Zumba & Dance Fitness",
    "Self Defense",
    "General Fitness",
  ],
  "Wellness & Health": [
    "Nutrition & Diet",
    "Ayurveda",
    "Mental Health",
    "Women's Wellness",
  ],
  "Lifestyle & Practice": [
    "Home Practice",
    "Daily Routine",
    "Equipment & Gear",
    "Class Experiences",
    "Instructor Tips",
    "Student Journey",
  ],
  General: ["General Discussion", "Success Stories", "Questions & Help"],
} as const;

// Helper function to get category group
export function getCategoryGroup(category: CommunityCategory): string | null {
  for (const [group, categories] of Object.entries(CATEGORY_GROUPS)) {
    if ((categories as readonly string[]).includes(category)) {
      return group;
    }
  }
  return null;
}

// Helper function to validate category
export function isValidCategory(
  category: string
): category is CommunityCategory {
  return COMMUNITY_CATEGORIES.includes(category as CommunityCategory);
}

export default COMMUNITY_CATEGORIES;
